import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, call } from "../lib/api.ts";
import { ago } from "../lib/format.ts";
import { useAdminLiveEvent } from "../lib/live.ts";
import { incidentTitle } from "../lib/safety.ts";
import { Icon } from "./Icon.tsx";

const HEARD = "ichegou.safety.heard";
const DISMISSED = "ichegou.safety.dismissed";

/** Ids kept in localStorage, so a reload neither replays the sound nor brings back a dismissed banner. */
function readIds(key: string) {
  try {
    return new Set<string>(JSON.parse(localStorage.getItem(key) ?? "[]"));
  } catch {
    return new Set<string>();
  }
}
function writeIds(key: string, ids: Set<string>) {
  try {
    localStorage.setItem(key, JSON.stringify([...ids].slice(-200)));
  } catch {
    // Private mode: the banner still works for this page.
  }
}

/** Two-tone alarm (three times for an SOS), synthesized: no audio file to load or cache. */
function playAlarm(urgent: boolean) {
  try {
    const ctx = new AudioContext();
    const t0 = ctx.currentTime + 0.05;
    for (let i = 0; i < (urgent ? 6 : 2); i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t = t0 + i * 0.28;
      osc.type = "square";
      osc.frequency.value = i % 2 ? 660 : 880;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.24);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.26);
    }
    setTimeout(() => void ctx.close(), 3000);
  } catch {
    // No audio (autoplay policy before any click): the banner is still there.
  }
}

/**
 * Open SOS alerts and incident reports in every city the staff member sees. `admin:live` brings
 * `safety.incident` at once; it keeps no history, so the list is also polled (and is the state).
 */
export function useSafetyAlerts(enabled: boolean) {
  const qc = useQueryClient();
  const live = useAdminLiveEvent(["safety.incident", "safety.incident_updated"], () => void qc.invalidateQueries({ queryKey: ["safety"] }), enabled);
  const q = useQuery({
    queryKey: ["safety", "incident-alerts"],
    queryFn: () => call(api.admin.safety.incidents.alerts.get()),
    enabled,
    refetchInterval: live ? 60_000 : 15_000,
    refetchIntervalInBackground: true,
    retry: false,
  });
  return { items: q.data?.items ?? [], live };
}

/** The admin-wide banner and sound for new SOS alerts and incident reports (Shell). */
export function SafetyBanner({ enabled }: { enabled: boolean }) {
  const { items } = useSafetyAlerts(enabled);
  const [dismissed, setDismissed] = useState(() => readIds(DISMISSED));

  useEffect(() => {
    const heard = readIds(HEARD);
    const fresh = items.filter((i) => !heard.has(i.id));
    if (!fresh.length) return;
    playAlarm(fresh.some((i) => i.kind === "sos"));
    for (const i of fresh) heard.add(i.id);
    writeIds(HEARD, heard);
  }, [items]);

  const shown = items.filter((i) => !dismissed.has(i.id));
  if (!shown.length) return null;
  const latest = shown[0]!;
  const sos = shown.filter((i) => i.kind === "sos").length;
  const dismiss = () => {
    const next = new Set([...dismissed, ...shown.map((i) => i.id)]);
    writeIds(DISMISSED, next);
    setDismissed(next);
  };

  return (
    <div className={`safety-banner${sos ? " sos" : ""}`} role="alert">
      <Icon name="alert" size={20} stroke={2.2} />
      <span className="grow">
        <b>{shown.length === 1 ? incidentTitle(latest) : `${shown.length} ocorrências abertas${sos ? ` · ${sos} SOS` : ""}`}</b>
        <span>{shown.length === 1 ? ` · ${ago(latest.createdAt)}` : ` · a última: ${incidentTitle(latest)}, ${ago(latest.createdAt)}`}</span>
      </span>
      <Link
        to="/seguranca"
        search={{ aba: "ocorrencias", situacao: "active", natureza: "all", tipo: "all", periodo: "7d", id: shown.length === 1 ? latest.id : undefined }}
        className="btn"
        style={{ textDecoration: "none" }}
      >
        Abrir
      </Link>
      <button type="button" className="btn btn-icon" aria-label="Dispensar aviso" title="Dispensar (continua na fila de Segurança)" onClick={dismiss}>
        <Icon name="x" size={16} stroke={2.2} />
      </button>
    </div>
  );
}
